import React, { useEffect, useState } from "react";
import DashboardShell from "@/components/DashboardShell";
import { useProject } from "@/context/ProjectContext";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Check, Globe } from "lucide-react";

const MAX_LANGS = 6;

export default function Settings() {
  const { current, projects, selectProject, updateCurrent, createProject } = useProject();
  const { user } = useAuth();
  const [name, setName] = useState("");
  const [domain, setDomain] = useState("");
  const [description, setDescription] = useState("");
  const [industry, setIndustry] = useState("");
  const [industries, setIndustries] = useState([]);
  const [competitors, setCompetitors] = useState("");
  const [languages, setLanguages] = useState(["en-GB"]);
  const [allLanguages, setAllLanguages] = useState([]);
  const [engines, setEngines] = useState([]);
  const [saved, setSaved] = useState(false);
  const [langError, setLangError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [newName, setNewName] = useState("");

  useEffect(() => {
    api.get("/prompt-library").then((r) => setIndustries(r.data));
    api.get("/languages").then((r) => setAllLanguages(r.data));
  }, []);

  useEffect(() => {
    if (!current) return;
    setName(current.name || "");
    setDomain(current.domain || "");
    setDescription(current.description || "");
    setIndustry(current.industry || "");
    setCompetitors((current.competitors || []).join(", "));
    setLanguages(current.languages || ["en-GB"]);
    setEngines(current.engines || []);
  }, [current]);

  const toggleLang = (code, enabled) => {
    setLangError(null);
    if (code === "en-GB") return; // default always on
    setLanguages((prev) => {
      if (enabled) {
        if (prev.includes(code)) return prev;
        if (prev.length >= MAX_LANGS) {
          setLangError(`Max ${MAX_LANGS} languages per project.`);
          return prev;
        }
        return [...prev, code];
      }
      return prev.filter((c) => c !== code);
    });
  };

  const save = async (e) => {
    e.preventDefault();
    setLoading(true);
    setLangError(null);
    try {
      await updateCurrent({
        name,
        domain: domain || null,
        description: description || null,
        industry: industry || null,
        competitors: competitors
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean),
        languages,
        engines: engines.length ? engines : null,
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 1500);
    } finally {
      setLoading(false);
    }
  };

  const addProject = async (e) => {
    e.preventDefault();
    if (!newName.trim()) return;
    await createProject({ name: newName.trim() });
    setNewName("");
  };

  return (
    <DashboardShell project={current} projects={projects} onProjectChange={selectProject}>
      <div className="mb-8">
        <div className="label-eyebrow">Workspace</div>
        <h1 className="display mt-2 text-4xl font-black tracking-tighter">Settings</h1>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        <section className="card-flat p-5 lg:col-span-7" data-testid="project-settings">
          <div className="label-eyebrow">Current project</div>
          <form onSubmit={save} className="mt-4 space-y-4">
            <Field label="Project name" value={name} onChange={setName} testid="settings-name" />
            <Field label="Domain" value={domain} onChange={setDomain} testid="settings-domain" />
            <div>
              <label className="label-eyebrow">Industry</label>
              <select
                value={industry}
                onChange={(e) => setIndustry(e.target.value)}
                data-testid="settings-industry"
                className="mt-1 w-full bg-white border border-zinc-200 rounded-[4px] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-black/10"
              >
                <option value="">— None —</option>
                {industries.map((i) => (
                  <option key={i.industry_id} value={i.industry_id}>
                    {i.label}
                  </option>
                ))}
              </select>
              <div className="mt-1 text-[11px] text-zinc-500">
                Used to surface the right starter prompts on the Library tab.
              </div>
            </div>
            <div>
              <label className="label-eyebrow">Description</label>
              <textarea
                rows={3}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                data-testid="settings-desc"
                className="mt-1 w-full bg-white border border-zinc-200 rounded-[4px] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-black/10"
              />
            </div>
            <Field
              label="Competitors (comma-separated)"
              value={competitors}
              onChange={setCompetitors}
              testid="settings-competitors"
            />

            <div data-testid="settings-languages">
              <label className="label-eyebrow">
                <Globe size={10} className="inline -mt-0.5 mr-1" />
                Languages (max {MAX_LANGS})
              </label>
              <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 gap-2">
                {allLanguages.map((l) => {
                  const enabled = languages.includes(l.code);
                  const locked = l.code === "en-GB";
                  return (
                    <label
                      key={l.code}
                      data-testid={`lang-${l.code}`}
                      aria-disabled={locked ? "true" : undefined}
                      className={`flex items-center gap-2 border rounded-[4px] px-3 py-2 text-sm cursor-pointer transition-colors ${
                        enabled
                          ? "border-zinc-900 bg-zinc-50"
                          : "border-zinc-200 bg-white hover:border-zinc-300"
                      } ${locked ? "opacity-100 cursor-default" : ""}`}
                    >
                      <input
                        type="checkbox"
                        checked={enabled}
                        disabled={locked}
                        onChange={(e) => toggleLang(l.code, e.target.checked)}
                        className="accent-black w-3.5 h-3.5"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="text-[13px] font-medium truncate">{l.label}</div>
                        <div className="mono text-[10px] text-zinc-500">{l.code}</div>
                      </div>
                      {locked && (
                        <span className="mono text-[9px] uppercase tracking-[0.14em] text-zinc-500">
                          default
                        </span>
                      )}
                    </label>
                  );
                })}
              </div>
              {langError && (
                <div
                  className="mt-2 text-xs text-rose-600"
                  data-testid="lang-error"
                >
                  {langError}
                </div>
              )}
              <div className="mt-2 text-[11px] text-zinc-500">
                {languages.length}/{MAX_LANGS} enabled · non-default languages
                are auto-translated via OpenAI on the next run.
              </div>
            </div>

            <div data-testid="engines-section">
              <div className="label-eyebrow flex items-center gap-1">Engines tracked</div>
              <p className="text-[11px] text-zinc-500 mt-1">
                Pick the AI engines that should run on every scheduled scan.
                Leave all unselected to use the default (OpenAI + Anthropic + Gemini).
              </p>
              <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="engines-grid">
                {[
                  ["openai", "ChatGPT", "GPT-5.2"],
                  ["anthropic", "Claude", "Sonnet 4.5"],
                  ["gemini", "Gemini", "3 Flash"],
                  ["perplexity", "Perplexity", "Beta"],
                  ["google_aio", "Google AI Overviews", "Beta"],
                ].map(([key, label, sub]) => {
                  const checked = engines.includes(key);
                  return (
                    <label
                      key={key}
                      htmlFor={`settings-eng-${key}`}
                      className="flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer transition-colors"
                      style={{
                        border: `1px solid ${checked ? "var(--aw-accent)" : "var(--aw-border)"}`,
                        background: checked ? "var(--aw-accent-soft)" : "white",
                      }}
                      data-testid={`settings-engine-${key}`}
                    >
                      <input
                        id={`settings-eng-${key}`}
                        type="checkbox"
                        checked={checked}
                        onChange={(e) =>
                          setEngines((prev) =>
                            e.target.checked ? [...prev, key] : prev.filter((k) => k !== key)
                          )
                        }
                        className="accent-emerald-600 w-3.5 h-3.5"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="text-[13px] font-medium truncate">{label}</div>
                        <div className="mono text-[10px] text-zinc-500">{sub}</div>
                      </div>
                    </label>
                  );
                })}
              </div>
            </div>

            <div className="flex items-center gap-3">
              <button
                type="submit"
                disabled={loading}
                data-testid="settings-save"
                className="bg-black text-white text-sm px-4 py-2 rounded-lg hover:bg-zinc-800 transition-colors disabled:opacity-50"
              >
                {loading ? "Saving…" : "Save"}
              </button>
              {saved && (
                <span className="inline-flex items-center gap-1 text-xs text-emerald-700">
                  <Check size={12} /> Saved
                </span>
              )}
            </div>
          </form>
        </section>

        <section className="card-flat p-5 lg:col-span-5 space-y-4" data-testid="account-card">
          <div className="label-eyebrow">Account</div>
          <div className="flex items-center gap-3">
            {user?.picture && <img src={user.picture} alt="" className="w-10 h-10 rounded-full" />}
            <div>
              <div className="text-sm font-medium">{user?.name}</div>
              <div className="text-xs text-zinc-500">{user?.email}</div>
            </div>
          </div>
          <div className="border-t border-zinc-200 pt-4">
            <div className="label-eyebrow">New project</div>
            <form onSubmit={addProject} className="mt-2 flex gap-2">
              <input
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="Another brand"
                data-testid="new-project-name"
                className="flex-1 bg-white border border-zinc-200 rounded-[4px] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-black/10"
              />
              <button
                data-testid="new-project-submit"
                className="bg-black text-white text-sm px-3 py-2 rounded-[4px] hover:bg-zinc-800 transition-colors"
              >
                Add
              </button>
            </form>
          </div>
        </section>
      </div>
    </DashboardShell>
  );
}

function Field({ label, value, onChange, testid }) {
  return (
    <div>
      <label className="label-eyebrow">{label}</label>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="mt-1 w-full bg-white border border-zinc-200 rounded-[4px] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-black/10"
      />
    </div>
  );
}
